import React from 'react';

import {
  Alert,
  Card,
  Col,
  Row,
  Spinner,
  Button
} from 'react-bootstrap';

import {
  ContestantRegistrationDetailsDTO,
  EventJudgeContestantDTO,
} from 'API/Generated/data-contracts';

// =====================================================
// PROPS
// =====================================================

type ContestantDetailsCardProps = {
  contestant: EventJudgeContestantDTO;

  loadDetails: (
    registeredContestantId: number,
  ) => Promise<ContestantRegistrationDetailsDTO | null>;

  onBack: () => void;
};

// =====================================================
// DISPLAY TYPES
// =====================================================

type RegistrationAward =
  NonNullable<
    ContestantRegistrationDetailsDTO['enteredAwards']
  >[number];

type AwardGroup = {
  typeId: number;

  title: string;

  entered: RegistrationAward[];

  notEntered: RegistrationAward[];

  notEligible: RegistrationAward[];

  allEligible: boolean;
};

// =====================================================
// COMPONENT
// =====================================================

const ContestantDetailsCard = ({
  contestant,
  loadDetails,
  onBack,
}: ContestantDetailsCardProps) => {
  const [
    details,
    setDetails,
  ] =
    React.useState<ContestantRegistrationDetailsDTO | null>(
      null,
    );

  const [
    loading,
    setLoading,
  ] =
    React.useState<boolean>(
      true,
    );

  const [
    error,
    setError,
  ] =
    React.useState<string | null>(
      null,
    );

  // =====================================================
  // LOAD DETAILS
  // =====================================================

  React.useEffect(() => {
    let isMounted = true;

    const fetchDetails =
      async (): Promise<void> => {
        setLoading(true);
        setError(null);

        try {
          const registeredContestantId =
            contestant.registeredContestantId;

          if (
            !registeredContestantId
          ) {
            throw new Error(
              'Missing registered contestant ID.',
            );
          }

          const result =
            await loadDetails(
              registeredContestantId,
            );

          if (!isMounted) {
            return;
          }

          setDetails(
            result ?? null,
          );

          if (!result) {
            setError(
              'Unable to load registration details.',
            );
          }
        } catch (err) {
          console.error(
            'Failed to load contestant registration details:',
            err,
          );

          if (!isMounted) {
            return;
          }

          setDetails(null);

          setError(
            'Unable to load registration details.',
          );
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };

    void fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [
    contestant.registeredContestantId,
    loadDetails,
  ]);

  // =====================================================
  // HELPERS
  // =====================================================

  const formatMoney = (
    value?: number | null,
  ): string => {
    return `$${Number(
      value ?? 0,
    ).toFixed(2)}`;
  };

  const getFullName =
    (): string => {
      return (
        contestant
          .contestantName
          ?.trim() ||
        'Contestant'
      );
    };

  const uniqueStrings = (
    values: Array<
      string | null | undefined
    >,
  ): string[] => {
    return Array.from(
      new Set(
        values
          .map(
            (value) =>
              value?.trim() ??
              '',
          )
          .filter(Boolean),
      ),
    );
  };

  const getAwardTypeName = (
    awardTypeId?: number | null,
    awardTypeName?: string | null,
  ): string => {
    if (
      awardTypeName?.trim()
    ) {
      return awardTypeName;
    }

    switch (
      Number(
        awardTypeId ??
          0,
      )
    ) {
      case 1:
        return 'Titles';

      case 2:
        return 'Side Awards';

      case 3:
        return 'Jackpots';

      default:
        return 'Other Awards';
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner
          animation="border"
        />

        <div className="fs-5 mt-3">
          Loading contestant
          details...
        </div>
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (
    error ||
    !details
  ) {
    return (
      <Alert
        variant="danger"
        className="mb-0"
      >
        {error ??
          'Unable to load registration details.'}
      </Alert>
    );
  }

  // =====================================================
  // DATA
  // =====================================================

  const invoice =
    details.invoice;

  const enteredAocs =
    details.enteredAOCs ??
    [];

  const notEnteredAocs =
    details.notEnteredAOCs ??
    [];

  const enteredAwards =
    details.enteredAwards ??
    [];

  const notEnteredAwards =
    details.notEnteredAwards ??
    [];

  // =====================================================
  // CONTESTANT ATTRIBUTES
  // =====================================================

  const divisionName =
    contestant.attributes
      ?.eventDivisionName ??
    '';

  const ageGroupName =
    contestant.attributes
      ?.ageGroupName ??
    '';

  const supremeGroupName =
    uniqueStrings(
      contestant.attributes
        ?.supremeGroupNames ??
        [],
    ).join(', ');

  const levelName =
    contestant.attributes
      ?.levelName ??
    '';

  const genderName =
    contestant.attributes
      ?.genderName ??
    '';

  // =====================================================
  // INVOICE
  // =====================================================

  const selectedPackages =
    invoice
      ?.selectedPackages ??
    [];

  const visibleFees =
    (
      invoice?.otherFees ??
      []
    ).filter(
      (fee) =>
        fee.isHidden !==
        true,
    );

  const invoiceAocs =
    invoice?.selectedAOCs ??
    [];

  const invoiceAwards =
    invoice?.selectedAwards ??
    [];

  // =====================================================
  // EXTRA ADD-ONS
  // =====================================================

  const extraAocAddons =
    invoiceAocs.filter(
      (aoc) =>
        aoc.isFullyIncluded !==
        true,
    );

  const extraAwardAddons =
    invoiceAwards.filter(
      (award) =>
        award.isFullyIncluded !==
        true,
    );

  const hasExtraAddons =
    extraAocAddons.length >
      0 ||
    extraAwardAddons.length >
      0;

  // =====================================================
  // AOC GROUPS
  // =====================================================

  const stageEnteredAocs =
    enteredAocs.filter(
      (aoc) =>
        Number(
          aoc.aocTypeId,
        ) === 1,
    );

  const photoEnteredAocs =
    enteredAocs.filter(
      (aoc) =>
        Number(
          aoc.aocTypeId,
        ) === 2,
    );

  const otherEnteredAocs =
    enteredAocs.filter(
      (aoc) =>
        Number(
          aoc.aocTypeId,
        ) !== 1 &&
        Number(
          aoc.aocTypeId,
        ) !== 2,
    );

  // =====================================================
  // AWARDS
  // =====================================================

  // Only awards that are BOTH entered AND explicitly eligible
  // appear in the normal bullet list.
  const eligibleEnteredAwards =
    enteredAwards.filter(
      (award) =>
        award.isEligible ===
        true,
    );

  // All award results from the backend.
  const allAwardResults =
    [
      ...enteredAwards,
      ...notEnteredAwards,
    ];

  // Only explicit TRUE counts as eligible.
  // false / null / undefined are treated as not eligible.
  const notEligibleAwards =
    Array.from(
      new Map(
        allAwardResults
          .filter(
            (award) =>
              award.isEligible !==
              true,
          )
          .map(
            (award) => [
              Number(
                award.id,
              ),
              award,
            ],
          ),
      ).values(),
    );

  // =====================================================
  // AWARD TYPE IDS
  // =====================================================

  const awardTypeIds =
    Array.from(
      new Set<number>([
        ...allAwardResults.map(
          (award) =>
            Number(
              award.awardTypeId ??
                0,
            ),
        ),
      ]),
    );

  const preferredAwardOrder =
    [
      1,
      2,
      3,
      0,
    ];

  const sortedAwardTypeIds =
    [...awardTypeIds].sort(
      (a, b) => {
        const aIndex =
          preferredAwardOrder.indexOf(
            a,
          );

        const bIndex =
          preferredAwardOrder.indexOf(
            b,
          );

        return (
          (aIndex === -1
            ? 99
            : aIndex) -
          (bIndex === -1
            ? 99
            : bIndex)
        );
      },
    );

  // =====================================================
  // AWARD GROUPS
  // =====================================================

  const awardGroups: AwardGroup[] =
    sortedAwardTypeIds.map(
      (typeId) => {
        // Every award of this type returned by backend.
        const allAwardsForType =
          Array.from(
            new Map(
              allAwardResults
                .filter(
                  (award) =>
                    Number(
                      award.awardTypeId ??
                        0,
                    ) === typeId,
                )
                .map(
                  (award) => [
                    Number(
                      award.id,
                    ),
                    award,
                  ],
                ),
            ).values(),
          );

        // Entered AND eligible.
        const entered =
          eligibleEnteredAwards.filter(
            (award) =>
              Number(
                award.awardTypeId ??
                  0,
              ) === typeId,
          );

        // Not entered, regardless of eligibility.
        const notEntered =
          notEnteredAwards.filter(
            (award) =>
              Number(
                award.awardTypeId ??
                  0,
              ) === typeId,
          );

        // Anything not explicitly eligible.
        const notEligible =
          allAwardsForType.filter(
            (award) =>
              award.isEligible !==
              true,
          );

        // Green check ONLY when every award in this
        // award type is explicitly eligible.
        const allEligible =
          allAwardsForType.length >
            0 &&
          allAwardsForType.every(
            (award) =>
              award.isEligible ===
              true,
          );

        const sample =
          allAwardsForType[0] ??
          entered[0] ??
          notEntered[0] ??
          notEligible[0];

        return {
          typeId,

          title:
            getAwardTypeName(
              sample?.awardTypeId,
              sample?.awardTypeName,
            ),

          entered,

          notEntered,

          notEligible,

          allEligible,
        };
      },
    );

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="w-100">
      {/* ================================================= */}
      {/* CONTESTANT HEADER */}
      {/* ================================================= */}
<div className="mb-3">
  <Button
    variant="link"
    className="p-0 fw-semibold text-decoration-none"
    onClick={onBack}
  >
    ← Back to{' '}
    {ageGroupName ||
      divisionName ||
      'Contestants'}
  </Button>
</div>
      <Card className="border-0 bg-light mb-4">
        <Card.Body className="p-4">
          <Row className="align-items-center g-3">
            <Col xs={12}>
              <div className="d-flex flex-wrap align-items-baseline gap-2">
                <div className="display-6 fw-bold">
                  #
                  {
                    contestant
                      .contestantNumber
                  }
                </div>

                <h2 className="fw-bold mb-0">
                  {getFullName()}
                </h2>
              </div>
            </Col>

            <Col xs={12}>
              <div className="d-flex flex-wrap gap-2 gap-md-4 fs-6">
                {divisionName && (
                  <div>
                    <span className="fw-bold">
                      Division:{' '}
                    </span>

                    <span className="text-muted">
                      {divisionName}
                    </span>
                  </div>
                )}

                {ageGroupName && (
                  <div>
                    <span className="fw-bold">
                      Age Group:{' '}
                    </span>

                    <span className="text-muted">
                      {ageGroupName}
                    </span>
                  </div>
                )}

                {supremeGroupName && (
                  <div>
                    <span className="fw-bold">
                      Supreme Group:{' '}
                    </span>

                    <span className="text-muted">
                      {supremeGroupName}
                    </span>
                  </div>
                )}

                {levelName && (
                  <div>
                    <span className="fw-bold">
                      Level:{' '}
                    </span>

                    <span className="text-muted">
                      {levelName}
                    </span>
                  </div>
                )}

                {genderName && (
                  <div>
                    <span className="fw-bold">
                      Gender:{' '}
                    </span>

                    <span className="text-muted">
                      {genderName}
                    </span>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ================================================= */}
      {/* MAIN TWO-COLUMN LAYOUT */}
      {/* ================================================= */}

      <Row className="g-4 align-items-start">
        {/* ================================================= */}
        {/* LEFT - REGISTRATION INVOICE */}
        {/* ================================================= */}

        <Col
          xs={12}
          md={6}
        >
          <Card className="shadow-sm h-100">
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-4">
                Registration Invoice
              </h4>

              {/* ========================================= */}
              {/* PACKAGES */}
              {/* ========================================= */}

              <div className="mb-4">
                <div className="fw-bold text-uppercase small text-muted mb-2">
                  Packages
                </div>

                {selectedPackages.length >
                0 ? (
                  selectedPackages.map(
                    (
                      pkg,
                      index,
                    ) => (
                      <div
                        key={
                          pkg.id ??
                          pkg.name ??
                          index
                        }
                        className="py-2 border-bottom"
                      >
                        <div className="d-flex justify-content-between gap-3">
                          <div className="fw-semibold">
                            {pkg.name ||
                              'Package'}
                          </div>

                          <div className="fw-semibold text-nowrap">
                            {formatMoney(
                              pkg.price,
                            )}
                          </div>
                        </div>

                        {pkg
                          .includedAOCs
                          ?.length ? (
                          <div className="small text-muted mt-1">
                            <span className="fw-semibold">
                              Includes AOCs:{' '}
                            </span>

                            {uniqueStrings(
                              pkg.includedAOCs.map(
                                (
                                  aoc,
                                ) =>
                                  aoc.name,
                              ),
                            ).join(
                              ', ',
                            )}
                          </div>
                        ) : null}

                        {pkg
                          .includedAwards
                          ?.length ? (
                          <div className="small text-muted mt-1">
                            <span className="fw-semibold">
                              Includes Awards:{' '}
                            </span>

                            {uniqueStrings(
                              pkg.includedAwards.map(
                                (
                                  award,
                                ) =>
                                  award.name,
                              ),
                            ).join(
                              ', ',
                            )}
                          </div>
                        ) : null}
                      </div>
                    ),
                  )
                ) : (
                  <div className="text-muted">
                    None
                  </div>
                )}
              </div>

              {/* ========================================= */}
              {/* FEES */}
              {/* ========================================= */}

              <div className="mb-4">
                <div className="fw-bold text-uppercase small text-muted mb-2">
                  Fees
                </div>

                {visibleFees.length >
                0 ? (
                  visibleFees.map(
                    (
                      fee,
                      index,
                    ) => (
                      <div
                        key={
                          fee.id ??
                          fee.name ??
                          index
                        }
                        className="d-flex justify-content-between gap-3 py-2 border-bottom"
                      >
                        <div>
                          {fee.name ||
                            'Fee'}
                        </div>

                        <div className="fw-semibold text-nowrap">
                          {formatMoney(
                            Number(
                              fee.price ??
                                0,
                            ) *
                              Number(
                                fee.quantity ??
                                  1,
                              ),
                          )}
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <div className="text-muted">
                    None
                  </div>
                )}
              </div>

              {/* ========================================= */}
              {/* EXTRA ADD-ONS */}
              {/* ========================================= */}

              <div className="mb-4">
                <div className="fw-bold text-uppercase small text-muted mb-2">
                  Extra Add-Ons
                </div>

                {hasExtraAddons ? (
                  <>
                    {extraAocAddons.map(
                      (
                        addon,
                        index,
                      ) => (
                        <div
                          key={`aoc-${
                            addon.id ??
                            index
                          }`}
                          className="d-flex justify-content-between gap-3 py-2 border-bottom"
                        >
                          <div>
                            {addon.name ||
                              'AOC'}

                            <span className="small text-muted ms-2">
                              AOC
                            </span>
                          </div>

                          <div className="fw-semibold text-nowrap">
                            {formatMoney(
                              addon.billableExtendedPrice ??
                                addon.selectedExtendedPrice ??
                                addon.price,
                            )}
                          </div>
                        </div>
                      ),
                    )}

                    {extraAwardAddons.map(
                      (
                        addon,
                        index,
                      ) => (
                        <div
                          key={`award-${
                            addon.id ??
                            index
                          }`}
                          className="d-flex justify-content-between gap-3 py-2 border-bottom"
                        >
                          <div>
                            {addon.name ||
                              'Award'}

                            <span className="small text-muted ms-2">
                              Award
                            </span>
                          </div>

                          <div className="fw-semibold text-nowrap">
                            {formatMoney(
                              addon.billableExtendedPrice ??
                                addon.selectedExtendedPrice ??
                                addon.price,
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </>
                ) : (
                  <div className="text-muted">
                    None
                  </div>
                )}
              </div>

              {/* ========================================= */}
              {/* TOTALS */}
              {/* ========================================= */}

              <div className="border-top pt-3">
                <div className="d-flex justify-content-between py-1">
                  <span className="text-muted">
                    Subtotal
                  </span>

                  <span className="fw-semibold">
                    {formatMoney(
                      invoice
                        ?.subtotal,
                    )}
                  </span>
                </div>

                {Number(
                  invoice
                    ?.totalDiscount ??
                    0,
                ) >
                  0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">
                      Discounts
                    </span>

                    <span className="fw-semibold">
                      -
                      {formatMoney(
                        invoice
                          ?.totalDiscount,
                      )}
                    </span>
                  </div>
                )}

                <div className="d-flex justify-content-between py-1">
                  <span className="text-muted">
                    Amount Paid
                  </span>

                  <span className="fw-bold text-success">
                    {formatMoney(
                      invoice
                        ?.amountPaid,
                    )}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center border-top mt-2 pt-3">
                  <span className="fw-bold fs-5">
                    Balance Due
                  </span>

                  <span
                    className={`fw-bold fs-4 ${
                      Number(
                        invoice
                          ?.balanceDue ??
                          0,
                      ) >
                      0
                        ? 'text-danger'
                        : 'text-success'
                    }`}
                  >
                    {formatMoney(
                      invoice
                        ?.balanceDue,
                    )}
                  </span>
                </div>

                {invoice?.status && (
                  <div className="text-end mt-1 small text-muted">
                    Status:{' '}

                    <span className="fw-semibold text-body">
                      {
                        invoice.status
                      }
                    </span>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* ================================================= */}
        {/* RIGHT */}
        {/* ================================================= */}

        <Col
          xs={12}
          md={6}
        >
          {/* ================================================= */}
          {/* AOCs */}
          {/* ================================================= */}

          <Card className="shadow-sm mb-4">
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-4">
                Entered Areas of Competition
              </h4>

              {/* ========================================= */}
              {/* STAGE */}
              {/* ========================================= */}

              {stageEnteredAocs.length >
                0 && (
                <div className="mb-3">
                  <div className="fw-bold mb-1">
                    Stage Events
                  </div>

                  <ul className="mb-0 ps-4">
                    {stageEnteredAocs.map(
                      (aoc) => (
                        <li
                          key={
                            aoc.id
                          }
                          className="mb-1"
                        >
                          {aoc.name}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

              {/* ========================================= */}
              {/* PHOTO */}
              {/* ========================================= */}

              {photoEnteredAocs.length >
                0 && (
                <div className="mb-3">
                  <div className="fw-bold mb-1">
                    Photo Events
                  </div>

                  <ul className="mb-0 ps-4">
                    {photoEnteredAocs.map(
                      (aoc) => (
                        <li
                          key={
                            aoc.id
                          }
                          className="mb-1"
                        >
                          {aoc.name}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

              {/* ========================================= */}
              {/* OTHER */}
              {/* ========================================= */}

              {otherEnteredAocs.length >
                0 && (
                <div className="mb-3">
                  <div className="fw-bold mb-1">
                    Other Events
                  </div>

                  <ul className="mb-0 ps-4">
                    {otherEnteredAocs.map(
                      (aoc) => (
                        <li
                          key={
                            aoc.id
                          }
                          className="mb-1"
                        >
                          {aoc.name}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

              {enteredAocs.length ===
                0 && (
                <div className="text-muted">
                  No Areas of Competition
                  entered.
                </div>
              )}

              <div className="border-top pt-3 mt-3">
                {notEnteredAocs.length >
                0 ? (
                  <div className="alert alert-danger mb-0 py-2 px-3">
                    <span className="fw-bold">
                      Not Entered
                      in:{' '}
                    </span>

                    {uniqueStrings(
                      notEnteredAocs.map(
                        (
                          aoc,
                        ) =>
                          aoc.name,
                      ),
                    ).join(
                      ', ',
                    )}
                  </div>
                ) : (
                  <div className="text-success fw-bold">
                    ✓ Entered in all
                    Areas of Competition
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* ================================================= */}
          {/* TITLES / AWARDS */}
          {/* ================================================= */}

          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-4">
                Entered Titles & Awards
              </h4>

              {awardGroups.length >
              0 ? (
                awardGroups.map(
                  (
                    group,
                    groupIndex,
                  ) => (
                    <div
                      key={
                        group.typeId
                      }
                      className={
                        groupIndex <
                        awardGroups.length -
                          1
                          ? 'mb-4 pb-4 border-bottom'
                          : ''
                      }
                    >
                      {/* ================================= */}
                      {/* TYPE */}
                      {/* ================================= */}

                      <div className="fw-bold fs-5 mb-2">
                        {
                          group.title
                        }
                      </div>

                      {/* ================================= */}
                      {/* ENTERED + ELIGIBLE */}
                      {/* ================================= */}

                      {group.entered.length >
                      0 ? (
                        <ul className="mb-3 ps-4">
                          {group.entered.map(
                            (
                              award,
                            ) => (
                              <li
                                key={
                                  award.id
                                }
                                className="mb-1"
                              >
                                {
                                  award.name
                                }
                              </li>
                            ),
                          )}
                        </ul>
                      ) : (
                        <div className="text-muted mb-3">
                          None Entered
                        </div>
                      )}

                      {/* ================================= */}
                      {/* FULL ELIGIBILITY */}
                      {/* ================================= */}

                     {group.typeId === 1 &&
  group.allEligible && (
  <div className="text-success fw-bold mb-3">
    ✓ Eligible for all Titles
  </div>
)}

                      {/* ================================= */}
                      {/* NOT ELIGIBLE */}
                      {/* ================================= */}

                      {!group.allEligible &&
                        group.notEligible.length >
                          0 && (
                          <div className="text-danger mb-3">
                            <span className="fw-bold">
                              Not Eligible
                              to win:{' '}
                            </span>

                            {uniqueStrings(
                              group.notEligible.map(
                                (
                                  award,
                                ) =>
                                  award.name,
                              ),
                            ).join(
                              ', ',
                            )}
                          </div>
                        )}

                      {/* ================================= */}
                      {/* NOT ENTERED */}
                      {/* ================================= */}

                      {group.notEntered.length >
                        0 && (
                        <div className="alert alert-danger mb-0 py-2 px-3">
                          <span className="fw-bold">
                            Not Entered
                            in:{' '}
                          </span>

                          {uniqueStrings(
                            group.notEntered.map(
                              (
                                award,
                              ) =>
                                award.name,
                            ),
                          ).join(
                            ', ',
                          )}
                        </div>
                      )}
                    </div>
                  ),
                )
              ) : (
                <div className="text-muted">
                  No titles or awards
                  configured.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ContestantDetailsCard;